import { useNotification } from "@expert/common-ui";
import type { ProductInfo } from "@expert/contentful";
import { Box, Button, Flex, Modal, Text, TextInput } from "@mantine/core";
import { useInputState } from "@mantine/hooks";
import { type JSX, useEffect, useState } from "react";
import { useAnalytics } from "@/analytics";
import type { EligibleProduct } from "@/sales-tools";
import { createEnrollment, postDeclination, useProductOfferStore } from "@/sales-tools";
import { EnrollmentStatus, SaleProgress } from "@/sales-tools/home-product/types";
import { PlanBenefits } from "@/sales-tools/unified-sales-view/components/PlanBenefits";
import { getSelectedSessionId, usePartner, useSessionId } from "@/sdk";
import classes from "./SaleTracker.module.css";

export function SaleTracker({
    opened,
    onClose,
    selectedProduct,
    productInfo,
}: {
    opened: boolean;
    onClose: () => void;
    selectedProduct: EligibleProduct;
    productInfo?: ProductInfo;
}): JSX.Element | null {
    const [value, setValue] = useInputState<string>("");
    const { dispatcher } = useAnalytics();
    const [isLoadingEnroll, setIsLoadingEnroll] = useState(false);
    const [isLoadingDecline, setIsLoadingDecline] = useState(false);
    const partner = usePartner();
    const sessionId = useSessionId();

    useEffect(() => {
        void dispatcher.dispatchAction("DialogShown", "SaleTrackingModal", { productSku: selectedProduct.productSku });
    }, [dispatcher, selectedProduct.productSku]);

    const notification = useNotification();

    const closeAndClear = () => {
        setValue("");
        onClose();
    };
    const onAccept = async () => {
        setIsLoadingEnroll(true);
        void dispatcher.dispatchAction("ButtonClick", "Enroll", {
            productSku: selectedProduct.productSku,
            confirmationCode: value,
        });

        const response = await createEnrollment({
            sessionId: getSelectedSessionId(),
            partner,
            productOfferId: selectedProduct.id,
            confirmationCode: value,
            productSku: selectedProduct.productSku,
        });

        if (response.enrollmentStatus === EnrollmentStatus.Success) {
            useProductOfferStore.getState().updateProduct(sessionId, selectedProduct.id, {
                ...selectedProduct,
                saleProgress: SaleProgress.Success,
            });
        } else {
            notification.error("There was an error processing the enrollment");
        }
        setIsLoadingEnroll(false);
        closeAndClear();
    };

    const onDecline = async () => {
        setIsLoadingDecline(true);
        void dispatcher.dispatchAction("ButtonClick", "Decline", { productSku: selectedProduct.productSku });

        const response = await postDeclination({
            sessionId: getSelectedSessionId(),
            partner,
            productOfferId: selectedProduct.id,
            productSku: selectedProduct.productSku,
        });
        if (response) {
            useProductOfferStore.getState().updateProduct(sessionId, selectedProduct.id, {
                ...selectedProduct,
                saleProgress: SaleProgress.Declined,
            });
        } else {
            notification.error("There was an error processing the declination");
        }
        setIsLoadingDecline(false);
        closeAndClear();
    };

    return (
        <Modal
            data-testid={`${selectedProduct.productSku}-sale-tracker`}
            centered
            opened={opened}
            withCloseButton={true}
            transitionProps={{ transition: "pop" }}
            size="lg"
            title={
                <Text size="md" c="dark.0">
                    Track sale
                </Text>
            }
            onClose={closeAndClear}
        >
            {productInfo && <PlanBenefits productInfo={productInfo} />}
            <Box mt="xl">
                <TextInput
                    key={selectedProduct.id}
                    styles={{ input: { backgroundColor: "dark.2" } }}
                    label={
                        <Text mb="3xs" fw="var(--mantine-font-weight-heavy)" size="sm" c="dark.1">
                            Enter the confirmation code
                        </Text>
                    }
                    onChange={setValue}
                    value={value}
                    size="sm"
                    classNames={{ input: classes.styledInput }}
                />
            </Box>
            <Flex justify="right" mt="xl">
                <Button
                    data-testid="decline-button"
                    loading={isLoadingDecline}
                    disabled={isLoadingEnroll || isLoadingDecline}
                    onClick={() => void onDecline()}
                    variant="subtle"
                    mr="sm"
                    size="sm"
                    color="white"
                >
                    <Text style={{ textDecoration: "underline" }} c="dark.1" size="xs">
                        They declined
                    </Text>
                </Button>
                <Button
                    data-testid="enroll-button"
                    loading={isLoadingEnroll}
                    disabled={isLoadingEnroll || isLoadingDecline || value.length === 0}
                    onClick={() => void onAccept()}
                    radius="xl"
                    size="sm"
                    fz="xs"
                    variant="filled"
                >
                    I enrolled the customer
                </Button>
            </Flex>
        </Modal>
    );
}
